/**
 * Browser analytics. Initializes PostHog (for its own dashboard / session
 * replay) and also captures pageview + CTA events into our own database via a
 * server function, so the admin analytics panels can read real numbers back
 * without needing a PostHog personal API key.
 */
import posthog from "posthog-js";
import { trackEvent } from "./analytics.functions";

let initialized = false;
let lastPath = "";

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  const KEY = "kcn_analytics_sid";
  let sid = window.sessionStorage.getItem(KEY);
  if (!sid) {
    sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(KEY, sid);
  }
  return sid;
}

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  const token = import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY;
  const region = import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION || "us";
  if (token) {
    try {
      posthog.init(token, {
        api_host: region === "eu" ? "https://eu.i.posthog.com" : "https://us.i.posthog.com",
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,
        person_profiles: "always",
      });
    } catch {
      /* PostHog init is best-effort; our own store is the source of truth. */
    }
  }
}

/** Internal surfaces we never count as audience traffic. */
export function isInternalPath(path: string) {
  return /^\/(admin|auth|login|_)/.test(path);
}

/** True for internal tooling loads (admin SEO checker iframes, embeds). */
export function isSyntheticLoad() {
  if (typeof window === "undefined") return true;
  try {
    if (window.top !== window.self) return true;
    if (new URLSearchParams(window.location.search).has("seo-check")) return true;
  } catch {
    return true;
  }
  return false;
}

export function trackPageview(path: string) {
  if (typeof window === "undefined" || path === lastPath) return;
  if (isInternalPath(path) || isSyntheticLoad()) return;
  lastPath = path;
  const referrer = document.referrer || null;
  const ua = navigator.userAgent || null;
  const sid = getSessionId();

  // PostHog capture (best-effort)
  try {
    if (posthog.__loaded) {
      posthog.capture("$pageview", { $current_url: window.location.origin + path, path });
    }
  } catch {
    /* ignore */
  }

  // Our own store (fire-and-forget; never block the UI)
  trackEvent({
    data: { type: "pageview", path, referrer, user_agent: ua, session_id: sid },
  }).catch(() => {});
}

export type AnalyticsCta =
  | "visibility_audit"
  | "round_table"
  | "strategy_session"
  | "contact"
  | "blog";

export function trackCtaClick(cta: AnalyticsCta, path: string) {
  const sid = getSessionId();
  try {
    if (posthog.__loaded) posthog.capture("cta_click", { cta, path });
  } catch {
    /* ignore */
  }
  trackEvent({
    data: { type: "cta_click", path, cta, session_id: sid },
  }).catch(() => {});
}

export function trackFormView(cta: AnalyticsCta, path: string) {
  if (isSyntheticLoad()) return;
  const sid = getSessionId();
  trackEvent({
    data: { type: "form_view", path, cta, session_id: sid },
  }).catch(() => {});
}
